'use client';

import type { AccordionItem } from '@workspace/interfaces';
import { AccordionProvider, useAccordion as useAccordionContext } from './useAccordion';
import type { AccordionProps, BaseAccordionItem } from './accordion.types';
import { AccordionEmptyState, AccordionItemContent, AccordionItemRow } from './ui';
import { cn } from '@workspace/lib';

const isAccordionItem = (item: BaseAccordionItem): item is AccordionItem =>
  typeof (item as AccordionItem).title === 'string' && Array.isArray((item as AccordionItem).items);

function AccordionBody<TItem extends BaseAccordionItem>({
  items,
  className,
  renderItemRow,
  renderItemContent,
  itemSpacingClassName = 'space-y-4',
}: AccordionProps<TItem>) {
  const { isOpen, toggle, getHeight, contentRefs } = useAccordionContext();

  if (!items || items.length === 0) {
    return <AccordionEmptyState className={className} />;
  }

  return (
    <div className={cn('w-full', itemSpacingClassName, className)}>
      {items.map((item: TItem) => {
        const isExpanded = isOpen(item.id);
        const handleToggle = () => toggle(item.id);

        return (
          <div key={item.id} className="transition-shadow duration-200">
            {renderItemRow
              ? renderItemRow(item, isExpanded, handleToggle)
              : isAccordionItem(item) && (
                  <AccordionItemRow item={item} isExpanded={isExpanded} onToggle={handleToggle} />
                )}
            {renderItemContent
              ? renderItemContent(item, isExpanded, getHeight, (el) => {
                  contentRefs.current[item.id] = el;
                })
              : isAccordionItem(item) && (
                  <AccordionItemContent
                    item={item}
                    isExpanded={isExpanded}
                    getHeight={getHeight}
                    contentRef={(el) => {
                      contentRefs.current[item.id] = el;
                    }}
                  />
                )}
          </div>
        );
      })}
    </div>
  );
}

export function Accordion<TItem extends BaseAccordionItem = AccordionItem>({
  items,
  className = '',
  defaultExpanded = [],
  renderItemRow,
  renderItemContent,
  selectionMode = 'single',
  itemSpacingClassName,
}: AccordionProps<TItem>) {
  const hasItems = !!items && items.length > 0;

  if (!hasItems) {
    return <AccordionEmptyState className={className} />;
  }

  return (
    <AccordionProvider defaultExpanded={defaultExpanded} selectionMode={selectionMode}>
      <AccordionBody
        items={items}
        className={className}
        renderItemRow={renderItemRow}
        renderItemContent={renderItemContent}
        itemSpacingClassName={itemSpacingClassName}
      />
    </AccordionProvider>
  );
}
